import { boostApi } from '@api/queries/boost';
import { factoryApi } from '@api/queries/factory';
import {
  type BoostItemType,
  type BoostType,
  type UserFactory,
  type UserFactoryNextToBuild,
} from '@common/types/factoryType';
import {
  type ClaimAllMaterialsResponse,
  type ClearFactoryProductionResponse,
  type GetFactoriesListResponse,
} from '@common/types/query/factory';
import { createSlice, isAnyOf, type PayloadAction } from '@reduxjs/toolkit';

import { sanitizeBoosts } from '@/common/lib/sanitizeBoosts';
import { type BoostInstantCompleteResponse } from '@/common/types/query/boost';

interface FactoryInitialState {
  list: UserFactory[] | null;
  nextToBuild: UserFactoryNextToBuild | null;
  boosts: BoostItemType[] | null;
}

const initialState: FactoryInitialState = {
  list: null,
  nextToBuild: null,
  boosts: null,
};

const factorySlice = createSlice({
  name: 'factory',
  initialState,
  reducers: {
    clearFactoryBoost: (
      state,
      { payload }: PayloadAction<{ plantId: string; boostType: BoostType }>,
    ) => {
      if (state.boosts) {
        const boost = state.boosts.find(
          (boost) =>
            boost?.boostType === payload.boostType &&
            boost?.plants?.includes(payload.plantId),
        );

        if (boost) {
          boost.expiresInSeconds = 0;
        }
      }
    },
    countDowntFactoryBoostTimers: (state) => {
      const boosts = state?.boosts;

      if (boosts) {
        for (const boost of boosts) {
          if (!boost) continue;

          if ((boost.expiresInSeconds || 0) > 0) {
            boost.expiresInSeconds = (boost.expiresInSeconds || 0) - 1;
          }

          if ((boost.nextUseInSeconds || 0) > 0) {
            boost.nextUseInSeconds = (boost.nextUseInSeconds || 0) - 1;
          }
        }
      }
    },
    buyCryptoBoostsFactory: (
      state,
      { payload }: PayloadAction<BoostItemType[]>,
    ) => {
      if (state.list) {
        const incoming =
          sanitizeBoosts<BoostItemType>(
            payload,
            'factory/buyCryptoBoostsFactory',
          ) || [];
        state.boosts = state.boosts
          ? [
              ...(sanitizeBoosts<BoostItemType>(
                state.boosts,
                'factory/buyCryptoBoostsFactory/existing',
              ) || []),
              ...incoming,
            ]
          : incoming;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addMatcher(
        factoryApi.endpoints.getFactories.matchFulfilled,
        (state, { payload }: PayloadAction<GetFactoriesListResponse>) => {
          state.list = payload.userFactories;
          state.nextToBuild = payload.nextToBuild;
          state.boosts = sanitizeBoosts<BoostItemType>(
            payload.boosts,
            'factory/getFactories',
          );
        },
      )
      .addMatcher(
        factoryApi.endpoints.buildFactory.matchFulfilled,
        (state, { payload }: PayloadAction<UserFactory[]>) => {
          state.list = payload;
        },
      )
      .addMatcher(
        factoryApi.endpoints.claimAllMaterials.matchFulfilled,
        (state, { payload }: PayloadAction<ClaimAllMaterialsResponse>) => {
          state.list = payload.plants;
        },
      )
      .addMatcher(
        factoryApi.endpoints.upgradeFactory.matchFulfilled,
        (state, { payload }) => {
          if (state.list) {
            const factory = state.list.find(
              (factory) => factory.id === payload.id,
            );

            if (factory) {
              Object.assign(factory, payload);
            }
          }
        },
      )
      .addMatcher(
        factoryApi.endpoints.claimMaterialById.matchFulfilled,
        (state, { payload }) => {
          const factory = state.list?.find(
            (factory) => factory.id === payload.plant.id,
          );

          if (factory) {
            Object.assign(factory, payload.plant);
          }
        },
      )
      .addMatcher(
        factoryApi.endpoints.putToFactory.matchFulfilled,
        (state, { payload }) => {
          const factory = state.list?.find(
            (factory) => factory.id === payload.id,
          );

          if (factory) {
            Object.assign(factory, payload);
          }
        },
      )
      .addMatcher(
        factoryApi.endpoints.putAllFactory.matchFulfilled,
        (state, { payload }) => {
          state.list = payload.plants;
        },
      )
      .addMatcher(
        isAnyOf(
          boostApi.endpoints.boostPlant.matchFulfilled,
          boostApi.endpoints.boostPlantAdvertising.matchFulfilled,
        ),
        (state, action) => {
          const plantType = action.meta.arg.originalArgs.plantType;

          if (plantType === 'Factory') {
            const payload = action.payload as GetFactoriesListResponse;
            state.list = payload.userFactories;
            state.nextToBuild = payload.nextToBuild;
            state.boosts = sanitizeBoosts<BoostItemType>(
              payload.boosts,
              'factory/boostPlant',
            );
          }
        },
      )
      .addMatcher(
        boostApi.endpoints.boostInstantComplete.matchFulfilled,
        (state, { payload }: PayloadAction<BoostInstantCompleteResponse>) => {
          const factory = state.list?.find(({ id }) => id === payload.plant.id);

          if (factory) {
            Object.assign(factory, payload.plant);
          }
        },
      )
      .addMatcher(
        factoryApi.endpoints.clearFactoryProduction.matchFulfilled,
        (state, { payload }: PayloadAction<ClearFactoryProductionResponse>) => {
          if (state.list) {
            const factory = state.list.find(
              ({ id }) => payload.factory.id === id,
            );

            if (factory) {
              Object.assign(factory, payload.factory);
            }
          }
        },
      );
  },
});

export const {
  clearFactoryBoost,
  countDowntFactoryBoostTimers,
  buyCryptoBoostsFactory,
} = factorySlice.actions;
export default factorySlice.reducer;
